import {
  AiOutlined,
  FilterOutlined,
  GlobeUniversal,
  OffersOutlined,
  UserOutlined,
} from '@duxton-web/icons';
import { Alert, Menu } from '@duxton-web/ui';
import i18n from 'i18n-js';
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { useHolosPortalFeatureFlag } from 'omega-rtc/utils/portal-config';

import AttrsPage from './AttrsPage';
import AudiencePage from './AudiencePage';
import './ContentConfig.scss';
import CountriesPage from './CountriesPage';
import CvpsPage from './CvpsPage';
import { NAV_ITEMS } from './data';
import PersonaPage from './PersonaPage';
import type { ContentConfigPermissions } from './permissions';
import { getContentConfigPermissions } from './permissions';
import ProductOfferingPage from './ProductOfferingPage';
import { contentConfigStyleVars } from './theme';
import VerticalsPage from './VerticalsPage';

const ICONS = {
  sparkle: AiOutlined,
  tag: OffersOutlined,
  user: UserOutlined,
  filter: FilterOutlined,
  globe: GlobeUniversal,
};

const PAGES = {
  'product-offerings': ProductOfferingPage,
  audiences: AudiencePage,
  cvps: CvpsPage,
  persona: PersonaPage,
  attrs: AttrsPage,
  countries: CountriesPage,
  verticals: VerticalsPage,
};

type Props = {
  contentConfigPermissions?: ContentConfigPermissions;
};

const ContentConfigPortal = ({ contentConfigPermissions: providedPermissions }: Props) => {
  const userPermissions = useSelector(
    (state: any) => state.app && state.app.user && state.app.user.userPermissions,
  );
  const contentConfigPermissions = providedPermissions
    || getContentConfigPermissions(userPermissions);
  const editsDisabled = useHolosPortalFeatureFlag(
    'disableContentConfigEdits',
  );
  const effectivePermissions = useMemo<ContentConfigPermissions>(
    () => (editsDisabled
      ? {
        ...contentConfigPermissions,
        canCreate: false,
        canUpdate: false,
        canReview: false,
      }
      : contentConfigPermissions),
    [contentConfigPermissions, editsDisabled],
  );
  const audiencesEnabled = useHolosPortalFeatureFlag(
    'enabledContentConfigAudiences',
  );
  const [active, setActive] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return audiencesEnabled && params.has('audienceKey')
      ? 'audiences'
      : 'product-offerings';
  });
  useEffect(() => {
    const hasAudienceDeepLink = new URLSearchParams(window.location.search)
      .has('audienceKey');
    if (audiencesEnabled && hasAudienceDeepLink) setActive('audiences');
    else if (!audiencesEnabled && active === 'audiences') setActive('product-offerings');
  }, [active, audiencesEnabled]);
  // Bumped on every nav click so a page can reset its own view (e.g. Product
  // Offerings drops back to the list) even when its nav item is already active.
  const [navTick, setNavTick] = useState(0);
  // Single-level nav — no group headers.
  const availableNavItems = useMemo(
    () => NAV_ITEMS.filter((item) => (
      (item.id !== 'audiences' || audiencesEnabled)
      && (item.id !== 'attrs' || !audiencesEnabled)
    )),
    [audiencesEnabled],
  );
  const menuItems = useMemo(() => availableNavItems.map((item) => {
    const IconComp = ICONS[item.icon] || OffersOutlined;
    return { key: item.id, label: i18n.t(item.labelKey), icon: <IconComp /> };
  }), [availableNavItems]);

  const ActivePage = active === 'audiences' && !audiencesEnabled
    ? ProductOfferingPage
    : PAGES[active] || ProductOfferingPage;

  return (
    <div className="cc-portal-shell" style={contentConfigStyleVars}>
      {editsDisabled && (
        <Alert
          className="cc-maintenance-banner"
          description={i18n.t('ucd_content_config.maintenance.description')}
          message={i18n.t('ucd_content_config.maintenance.title')}
          showIcon
          type="warning"
        />
      )}
      <div className="cc-portal">
        <Menu
          className="cc-rail"
          mode="inline"
          selectedKeys={[active]}
          onClick={({ key }) => { setActive(key); setNavTick((t) => t + 1); }}
          items={menuItems}
        />
        <div className="cc-main">
          <ActivePage
            contentConfigPermissions={effectivePermissions}
            navTick={navTick}
          />
        </div>
      </div>
    </div>
  );
};

export default ContentConfigPortal;
